import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Instagram, Facebook, Heart, MessageCircle, ExternalLink, Sparkles, Users } from 'lucide-react';

/**
 * Social Feed Section - Instagram & Facebook Integration
 * 
 * Configuration via env vars:
 * - REACT_APP_INSTAGRAM_TOKEN: Instagram Graph API access token (for Business accounts)
 *   Get from: https://developers.facebook.com/docs/instagram-basic-display-api
 * - REACT_APP_INSTAGRAM_HANDLE: Instagram handle (e.g., "kayagroup" - without @)
 * - REACT_APP_FACEBOOK_PAGE: Facebook page name (e.g., "KayaPropertyManagement")
 * 
 * Falls back to elegant placeholder + "Follow Us" CTAs when tokens not configured
 */

const PLACEHOLDER_POSTS = [
  {
    id: 'demo1',
    media_url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80',
    caption: 'Vilë luksoze në Prishtinë – Sapo listuar për shitje! 🏡',
    permalink: '#',
    timestamp: '2 orë më parë',
  },
  {
    id: 'demo2',
    media_url: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80',
    caption: 'Investoni në Tiranë – ROI 8-12% vjetor 📈',
    permalink: '#',
    timestamp: '1 ditë më parë',
  },
  {
    id: 'demo3',
    media_url: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=600&q=80',
    caption: 'Apartament ekskluziv me pamje detare në Durrës 🌊',
    permalink: '#',
    timestamp: '3 ditë më parë',
  },
  {
    id: 'demo4',
    media_url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80',
    caption: 'Interior design që frymëzon – Prishtinë',
    permalink: '#',
    timestamp: '5 ditë më parë',
  },
  {
    id: 'demo5',
    media_url: 'https://images.unsplash.com/photo-1600566753151-384129cf4e3e?w=600&q=80',
    caption: 'Këshilla nga eksperti: si të investoni në 2026',
    permalink: '#',
    timestamp: '1 javë më parë',
  },
  {
    id: 'demo6',
    media_url: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=600&q=80',
    caption: 'Klient i kënaqur – prona e ëndrrave në Kosovë ✨',
    permalink: '#',
    timestamp: '2 javë më parë',
  },
];

export const SocialFeedSection = ({ lang = 'sq' }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  const handle = process.env.REACT_APP_INSTAGRAM_HANDLE || 'kaya.group';
  const facebookPage = process.env.REACT_APP_FACEBOOK_PAGE || 'kaya.group';
  const igToken = process.env.REACT_APP_INSTAGRAM_TOKEN;

  const labels = lang === 'sq' ? {
    badge: 'Rrjete Sociale',
    title: 'Ndiqni Aktivitetin Tonë',
    subtitle: 'Shikoni pronat e reja, kulisat dhe këshillat e ekspertëve në kohë reale',
    followIg: 'Ndiqe në Instagram',
    followFb: 'Ndiqe në Facebook',
    demo: 'Postimet e demonstruara',
    demoDesc: 'Konfiguro tokenat në .env për të lidhur postimet reale',
    likes: 'Pëlqime',
    followers: 'Ndjekës',
    latest: 'Postimet e Fundit',
  } : {
    badge: 'Social Media',
    title: 'Follow Our Journey',
    subtitle: 'See new properties, behind-the-scenes and expert tips in real time',
    followIg: 'Follow on Instagram',
    followFb: 'Follow on Facebook',
    demo: 'Demo posts shown',
    demoDesc: 'Configure tokens in .env to connect real posts',
    likes: 'Likes',
    followers: 'Followers',
    latest: 'Latest Posts',
  };

  useEffect(() => {
    if (!igToken) {
      setPosts(PLACEHOLDER_POSTS);
      setIsDemo(true);
      setLoading(false);
      return;
    }

    // Fetch Instagram Media via Graph API
    fetch(`https://graph.instagram.com/me/media?fields=id,caption,media_url,media_type,permalink,timestamp&access_token=${igToken}&limit=6`)
      .then(res => res.json())
      .then(data => {
        if (data.data) {
          setPosts(data.data.slice(0, 6));
          setIsDemo(false);
        } else {
          throw new Error('No data');
        }
        setLoading(false);
      })
      .catch(err => {
        console.warn('[SocialFeed] Falling back to demo:', err);
        setPosts(PLACEHOLDER_POSTS);
        setIsDemo(true);
        setLoading(false);
      });
  }, [igToken]);

  return (
    <section id="social" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Decorative gradient orbs */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(197,160,94,0.3) 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(197,160,94,0.2) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 space-y-6">
          <div className="inline-flex items-center gap-3 px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider animate-fadeInUp">
            <Sparkles className="w-4 h-4" />
            {labels.badge}
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {labels.title}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full animate-pulse" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            {labels.subtitle}
          </p>
        </div>

        {/* Social CTAs Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {/* Instagram Card */}
          <a
            href={`https://instagram.com/${handle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden rounded-3xl animate-fadeInUp"
            style={{ animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards' }}
            data-testid="social-instagram"
          >
            {/* Instagram gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] opacity-90 group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-black/30" />
            
            {/* Animated pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-4 border-white rounded-full" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-2 border-white rounded-full opacity-50" />
            </div>
            
            <div className="relative p-8 md:p-10 flex items-center justify-between">
              <div className="space-y-2">
                <Instagram className="w-12 h-12 text-white mb-2" />
                <div className="text-white/80 text-sm font-semibold uppercase tracking-wider">Instagram</div>
                <div className="text-white text-2xl md:text-3xl font-bold">@{handle}</div>
                <div className="text-white/70 text-sm">{labels.followIg}</div>
              </div>
              <ExternalLink className="w-8 h-8 text-white/70 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
            </div>
          </a>

          {/* Facebook Card */}
          <a
            href={`https://facebook.com/${facebookPage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden rounded-3xl animate-fadeInUp"
            style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}
            data-testid="social-facebook"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#1877F2] via-[#0866FF] to-[#004AAD] group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-black/20" />
            
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-4 border-white rounded-full" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-2 border-white rounded-full opacity-50" />
            </div>
            
            <div className="relative p-8 md:p-10 flex items-center justify-between">
              <div className="space-y-2">
                <Facebook className="w-12 h-12 text-white mb-2" />
                <div className="text-white/80 text-sm font-semibold uppercase tracking-wider">Facebook</div>
                <div className="text-white text-2xl md:text-3xl font-bold">@{facebookPage}</div>
                <div className="text-white/70 text-sm">{labels.followFb}</div>
              </div>
              <ExternalLink className="w-8 h-8 text-white/70 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
            </div>
          </a>
        </div>

        {/* Latest Posts Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-1 h-8 bg-gradient-to-b from-[#C5A05E] to-transparent rounded-full" />
              {labels.latest}
            </h3>
            {isDemo && (
              <div className="text-xs text-[#C5A05E]/70 backdrop-blur-xl bg-[#C5A05E]/10 border border-[#C5A05E]/20 px-3 py-1.5 rounded-full">
                {labels.demo}
              </div>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square bg-white/5 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {posts.map((post, idx) => (
                <a
                  key={post.id}
                  href={post.permalink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-black animate-fadeInUp"
                  style={{ animationDelay: `${0.6 + idx * 0.08}s`, opacity: 0, animationFillMode: 'forwards' }}
                  data-testid={`social-post-${idx}`}
                >
                  <img
                    src={post.media_url}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-4">
                    <p className="text-white text-sm font-medium line-clamp-3 mb-2">{post.caption}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-white/80 text-xs">
                        {post.timestamp && <span>{typeof post.timestamp === 'string' ? post.timestamp : new Date(post.timestamp).toLocaleDateString()}</span>}
                      </div>
                      <Instagram className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Follow CTA */}
        <div className="text-center animate-fadeInUp" style={{ animationDelay: '1s', opacity: 0, animationFillMode: 'forwards' }}>
          <div className="inline-flex items-center gap-4 px-6 py-4 backdrop-blur-xl bg-white/5 border border-[#C5A05E]/30 rounded-2xl">
            <Users className="w-6 h-6 text-[#C5A05E]" />
            <span className="text-gray-300">
              {lang === 'sq' ? 'Bashkohu me' : 'Join'} <span className="font-bold text-white">10,000+</span> {lang === 'sq' ? 'ndjekës të kënaqur' : 'happy followers'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
